import {loadStripe} from '@stripe/stripe-js'
import {transformDialCode} from 'business/paymentForm'
import {logToDataDog} from 'services/datadogLogger'

import {MerchantOfRecord} from '@vio/core/src/api-types/bovio/response/booking'
import isFhMor from '@vio/core/src/offer/business/isFhMor'

import type {
  PaymentRequest,
  PaymentRequestOptions,
  PaymentRequestPaymentMethodEvent,
  RadarSessionPayload,
  Stripe as StripeType,
  StripeCardNumberElement
} from '@stripe/stripe-js'
import type {CountryType} from '@vio/core/src/localization/types/Country'
import type {PaymentFormValues} from 'components/PaymentForm/initialFormValues'

type RadarSession = RadarSessionPayload['radarSession']

export interface StripeElementsPaymentMethod {
  id: string
  country?: string
}

let stripeLib: StripeType | null | undefined
let paymentRequest: PaymentRequest
let radarSession: RadarSession | undefined

const setup = async (apiKey: string): Promise<void> => {
  // Already initialized - do nothing
  if (stripeLib) return

  try {
    logToDataDog({
      logName: 'services.PSD2.Stripe.LoadingScript',
      metadata: {}
    })
    const instance = await loadStripe(apiKey)
    if (!instance) throw new Error('Stripe failed to load')
    stripeLib = instance as unknown as StripeType
    initRadarSession()
  } catch (error) {
    logToDataDog({
      logName: 'services.PSD2.Stripe.LoadingScriptError',
      metadata: {meta: error}
    })
    console.log('error in set up')
  }
}

const getStripeInstance = () => stripeLib

const buildBillingDetails = (
  formValues: PaymentFormValues,
  countriesList: CountryType[]
) => {
  const {firstName, lastName, email, phone} = formValues.customer
  const name = [firstName, lastName].join(' ')
  const dialCodePhone = transformDialCode(phone, countriesList)
  const fullPhone = dialCodePhone.countryCode
    ? `+${dialCodePhone.countryCode.replace(/-/g, '')}${dialCodePhone.number}`
    : dialCodePhone.number

  return {
    name,
    email,
    phone: fullPhone
  }
}

const getStripeElementsPaymentMethod = async (
  cardNumber: StripeCardNumberElement | null | undefined,
  formValues: PaymentFormValues,
  countriesList: CountryType[]
): Promise<StripeElementsPaymentMethod | undefined> => {
  const stripe = stripeLib
  if (!stripe || !cardNumber) {
    logToDataDog({
      logName: 'services.Stripe.CreatePaymentMethod.NotReady',
      metadata: {
        hasStripe: Boolean(stripe),
        hasCard: Boolean(cardNumber)
      },
      level: 'error'
    })
    return undefined
  }
  const billingDetails = buildBillingDetails(formValues, countriesList)
  try {
    const {paymentMethod, error} = await stripe.createPaymentMethod({
      type: 'card',
      card: cardNumber,
      billing_details: billingDetails
    })
    if (error || !paymentMethod) {
      logToDataDog({
        logName: 'services.Stripe.CreatePaymentMethod.Error',
        metadata: {
          code: error?.code,
          errorType: error?.type,
          errorMessage: error?.message
        },
        level: 'error'
      })
      return undefined
    }
    return {
      id: paymentMethod.id,
      // Stripe derives this from the card BIN, so it is the issuing country
      // rather than anything the booker typed. Absent for some card types.
      country: paymentMethod.card?.country ?? undefined
    }
  } catch (err) {
    logToDataDog({
      logName: 'services.Stripe.CreatePaymentMethod.Exception',
      metadata: {
        errorMessage: err instanceof Error ? err.message : String(err)
      },
      level: 'error'
    })
    return undefined
  }
}

const isStripeElementsEnabled = (
  merchantOfRecord: string | MerchantOfRecord | null | undefined
): boolean => Boolean(isFhMor({merchantOfRecord}))

const checkPaymentRequestAPIAvailability = async (
  paymentRequestData: PaymentRequestOptions
) => {
  if (paymentRequest) {
    // Update existing PaymentRequest with new currency/amount
    // instead of creating a new one (Stripe only allows one instance)
    paymentRequest.update(paymentRequestData)
  } else {
    paymentRequest = stripeLib!.paymentRequest({
      requestPayerName: true,
      requestPayerEmail: true,
      ...paymentRequestData
    })
  }

  // Check the availability of the Payment Request API first.
  try {
    const result = await paymentRequest.canMakePayment()
    if (result) {
      return result
    }

    return {}
  } catch (error) {
    logToDataDog({
      logName: 'services.PSD2.Stripe.CanMakePaymentError',
      metadata: {
        error: error instanceof Error ? error.message : String(error),
        country: paymentRequestData.country,
        currency: paymentRequestData.currency
      },
      level: 'error'
    })

    return {}
  }
}

const onPaymentMethodCreation = (
  callback: (ev: PaymentRequestPaymentMethodEvent) => void
) =>
  paymentRequest?.on(
    'paymentmethod',
    (ev: PaymentRequestPaymentMethodEvent) => {
      ev.complete('success')
      callback(ev)
    }
  )

const openPaymentValidationForm = () => {
  paymentRequest.show()
}

const initRadarSession = async () => {
  try {
    if (!stripeLib) throw new Error('Stripe library not initialized')

    const {radarSession: session, error} = await stripeLib.createRadarSession()

    if (error) throw error

    setRadarSession(session)
  } catch (error) {
    logToDataDog({
      logName: 'services.PSD2.Stripe.RadarSessionError',
      metadata: {
        meta: error
      }
    })
    console.log('error in radar session init', error)
  }
}

const getRadarSession = () =>
  radarSession ? {paymentSessionId: radarSession.id} : undefined

const setRadarSession = (session: RadarSession) => {
  radarSession = session
}

const handleCardAction = async (clientSecret: string) => {
  return await stripeLib!.handleCardAction(clientSecret)
}

const handleNextAction = async (clientSecret: string) => {
  return await stripeLib!.handleNextAction({clientSecret})
}

export const stripe = {
  setup,
  getStripeInstance,
  getStripeElementsPaymentMethod,
  isStripeElementsEnabled,
  checkPaymentRequestAPIAvailability,
  onPaymentMethodCreation,
  openPaymentValidationForm,
  initRadarSession,
  getRadarSession,
  setRadarSession,
  handleCardAction,
  handleNextAction
}
